import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Star, Search, MessageSquare, XCircle, RefreshCw } from "lucide-react";
import Swal from 'sweetalert2';
import { API_BASE_URL } from "../config";

export default function Reviews() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReviews = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/ratings/allratingsList`)
      .then(res => res.json())
      .then(resJson => {
        if (resJson && resJson.status && resJson.data) {
          const mapped = resJson.data.map(r => ({
            id: r.rating_id_PK,
            user: r.fullname || "N/A",
            email: r.email || "N/A",
            rating: parseFloat(r.rating) || 5.0,
            comment: r.review || "N/A",
            date: r.created_at
              ? new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              : "N/A",
            appVersion: r.template_name ? `Template: ${r.template_name}` : `Template #${r.template_id_FK}`,
            templateImage: r.template_image
          }));
          setReviews(mapped);
        } else {
          setReviews([]);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching reviews:", err);
        setReviews([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "This will permanently delete the review.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deleting...",
          text: "Please wait while the review is deleted.",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        fetch(`${API_BASE_URL}/api/ratings/${id}`, {
          method: "DELETE"
        })
          .then(res => {
            if (!res.ok) throw new Error("Failed to delete review");
            return res.json();
          })
          .then(resJson => {
            Swal.close();
            if (resJson && resJson.status) {
              Swal.fire('Deleted!', 'Review has been deleted.', 'success');
              fetchReviews(); // Refresh review list from backend
            } else {
              throw new Error(resJson.message || "Failed to delete review");
            }
          })
          .catch(err => {
            Swal.close();
            console.error("Delete review error:", err);
            Swal.fire("Error", err.message, "error");
          });
      }
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star key={star} size={14} className={star <= rating ? "text-amber-400 fill-amber-400" : "text-slate-200 fill-slate-200"} />
        ))}
      </div>
    );
  };

  const avgRating = reviews.length > 0
    ? (reviews.reduce((acc, curr) => acc + curr.rating, 0) / reviews.length).toFixed(1)
    : "0.0";

  return (
    <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans selection:bg-blue-100 dark:selection:bg-blue-900">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative w-full lg:w-[calc(100%-16rem)]">
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
          title="Reviews & Feedback"
          subtitle="Manage user ratings and feedback from the app."
        />

        <div className="p-4 sm:p-8 flex-1 w-full">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>


            </div>

            <div className="flex items-center gap-3">
              <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center gap-4">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase font-bold tracking-wider">Avg Rating</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-2xl font-bold text-slate-800 dark:text-slate-100">{avgRating}</span>
                    <Star size={20} className="text-amber-400 fill-amber-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 shadow-sm rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full">
                {/* Tabs */}
                <div className="flex items-center gap-1 bg-slate-50 p-1 rounded-lg border border-slate-200 dark:border-slate-700">
                  {['All', 'Positive', 'Negative'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === tab ? 'bg-white dark:bg-slate-800 text-blue-600 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                <button onClick={fetchReviews} className="flex items-center gap-2 bg-slate-50 dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" title="Reload Reviews">
                  <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                  Reload
                </button>
              </div>

              <div className="relative w-full sm:w-64">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search reviews..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-lg pl-9 pr-4 py-2 text-sm outline-none focus:border-blue-500 transition-all placeholder-slate-400"
                />
              </div>
            </div>

            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {loading ? (
                <div className="p-20 text-center flex flex-col items-center justify-center text-slate-500 dark:text-slate-400">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                  <p className="font-medium">Loading reviews...</p>
                </div>
              ) : reviews.length === 0 ? (
                <div className="p-20 text-center flex flex-col items-center justify-center text-slate-500 dark:text-slate-400">
                  <MessageSquare size={32} className="text-slate-300 mb-3" />
                  <p className="font-medium">No reviews found</p>
                  <p className="text-sm mt-1">There are no reviews to show.</p>
                </div>
              ) : (
                reviews
                  .filter(r => {
                    if (activeTab === 'All') return true;
                    if (activeTab === 'Positive') return r.rating >= 4;
                    if (activeTab === 'Negative') return r.rating < 4;
                    return true;
                  })
                  .filter(r => r.comment.toLowerCase().includes(searchTerm.toLowerCase()) || r.user.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map((review) => (
                    <div key={review.id} className="p-5 hover:bg-slate-50/50 dark:bg-slate-800/50 dark:bg-slate-900/50 transition-colors flex flex-col sm:flex-row gap-4 sm:gap-6">
                      <div className="shrink-0 flex flex-col items-start gap-2 w-48">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs shrink-0">
                            {review.user.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{review.user}</p>
                            <p className="text-[10px] text-slate-500 dark:text-slate-400">{review.email}</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {renderStars(review.rating)}
                          <span className="text-xs text-slate-400">• {review.date} • {review.appVersion}</span>
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed">{review.comment}</p>
                      </div>

                      <div className="shrink-0 flex items-center gap-2 sm:flex-col sm:items-end">
                        <button onClick={() => handleDelete(review.id)} className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 text-red-500 hover:bg-rose-100 hover:text-red-700 rounded-lg text-sm font-medium transition-colors border border-rose-100">
                          <XCircle size={16} /> Delete
                        </button>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
